/**
 * Engaging Networks Form Configuration
 *
 * Language-specific configuration for EN form endpoints and field mappings.
 * Each language can have different endpoints and field names.
 */

export interface ENFormConfig {
  endpoint: string; // Must always be page 2 (final page) per EN documentation
  formId: string;
  fieldMappings: {
    email: string;
    firstName: string;
    lastName: string;
    country: string;
    marketingConsent: string;
  };
  commentField?: string; // Optional comment field (language-specific)
  completionField?: string; // Optional completion field (language-specific)
  sessionField?: string;
  hiddenFields?: string[];
}

/**
 * EN form configurations for each language
 */
const enFormConfigs: Record<string, ENFormConfig> = {
  en: {
    endpoint: '/page/180219/petition/2',
    formId: 'pb_180219',
    fieldMappings: {
      email: 'supporter.emailAddress',
      firstName: 'supporter.firstName',
      lastName: 'supporter.lastName',
      country: 'supporter.country',
      marketingConsent: 'supporter.questions.27012',
    },
    sessionField: 'sessionId',
    hiddenFields: ['hidden'],
  },
  pl: {
    endpoint: '/page/180989/data/2',
    formId: 'pb_180989',
    fieldMappings: {
      email: 'supporter.emailAddress',
      firstName: 'supporter.firstName',
      lastName: 'supporter.lastName',
      country: 'supporter.country',
      marketingConsent: 'supporter.questions.175136', // Radio button field (Y/N), mandatory
    },
    commentField: 'supporter.questions.2284902', // Comment field for Polish
    completionField: 'supporter.questions.2284901', // Completion field for Polish
    sessionField: 'sessionId',
    hiddenFields: ['hidden'],
  },
  cs: {
    endpoint: '/page/181585/data/2',
    formId: 'pb_181585',
    fieldMappings: {
      email: 'supporter.emailAddress',
      firstName: 'supporter.firstName',
      lastName: 'supporter.lastName',
      country: 'supporter.country',
      marketingConsent: 'supporter.questions.228355', // Radio button field (Y/N), mandatory
    },
    commentField: 'supporter.questions.2288368', // Comment field for Czech
    completionField: 'supporter.questions.2288367', // Completion field for Czech
    sessionField: 'sessionId',
    hiddenFields: ['hidden'],
  },
  fr: {
    endpoint: '/page/181592/data/2',
    formId: 'pb_181592',
    fieldMappings: {
      email: 'supporter.emailAddress',
      firstName: 'supporter.firstName',
      lastName: 'supporter.lastName',
      country: 'supporter.country',
      marketingConsent: 'supporter.questions.174537', // Radio button field (Y/N), mandatory
    },
    commentField: 'supporter.questions.2288388', // Comment field for French
    completionField: 'supporter.questions.2288387', // Completion field for French
    sessionField: 'sessionId',
    hiddenFields: ['hidden'],
  },
  nl: {
    endpoint: '/page/181599/data/2',
    formId: 'pb_181599',
    fieldMappings: {
      email: 'supporter.emailAddress',
      firstName: 'supporter.firstName',
      lastName: 'supporter.lastName',
      country: 'supporter.country',
      marketingConsent: 'supporter.questions.174511', // Radio button field (Y/N), mandatory
    },
    commentField: 'supporter.questions.2288460', // Comment field for Dutch
    completionField: 'supporter.questions.2288461', // Completion field for Dutch
    sessionField: 'sessionId',
    hiddenFields: ['hidden'],
  },
  es: {
    endpoint: '/page/181609/data/2',
    formId: 'pb_181609',
    fieldMappings: {
      email: 'supporter.emailAddress',
      firstName: 'supporter.firstName',
      lastName: 'supporter.lastName',
      country: 'supporter.country',
      marketingConsent: 'supporter.questions.228490', // Radio button field (Y/N), mandatory
    },
    commentField: 'supporter.questions.2288594', // Comment field for Spanish
    completionField: 'supporter.questions.2288595', // Completion field for Spanish
    sessionField: 'sessionId',
    hiddenFields: ['hidden'],
  },
  it: {
    endpoint: '/page/181607/data/2',
    formId: 'pb_181607',
    fieldMappings: {
      email: 'supporter.emailAddress',
      firstName: 'supporter.firstName',
      lastName: 'supporter.lastName',
      country: 'supporter.country',
      marketingConsent: 'supporter.questions.174616', // Radio button field (Y/N), mandatory
    },
    commentField: 'supporter.questions.2288584', // Comment field for Italian
    completionField: 'supporter.questions.2288585', // Completion field for Italian
    sessionField: 'sessionId',
    hiddenFields: ['hidden'],
  },
};

/**
 * Gets the EN form configuration for a specific language
 * @param language Language code (e.g., 'en', 'pl')
 * @returns EN form configuration
 * @throws Error if language is not supported
 */
export function getENFormConfig(language: string): ENFormConfig {
  // Enhanced debugging: Log raw input
  console.log('[EN Form Config] Raw language input:', {
    value: language,
    type: typeof language,
    length: language?.length,
    charCodes: language?.split('').map(c => c.charCodeAt(0))
  });
  
  // Normalize language code (e.g., 'en-US' -> 'en')
  // Trim whitespace and handle edge cases
  const trimmedLang = (language || '').trim();
  const normalizedLang = trimmedLang.split('-')[0].toLowerCase();
  
  // Enhanced debugging: Log normalization process
  console.log('[EN Form Config] Normalization:', {
    trimmed: trimmedLang,
    normalized: normalizedLang,
    availableKeys: Object.keys(enFormConfigs),
    keyExists: normalizedLang in enFormConfigs,
    directLookup: enFormConfigs[normalizedLang] ? 'found' : 'not found'
  });
  
  // Try direct lookup first
  let config = enFormConfigs[normalizedLang];
  
  // If not found, try explicit key comparison (handles edge cases)
  if (!config) {
    const availableKeys = Object.keys(enFormConfigs);
    const matchingKey = availableKeys.find(key => key.toLowerCase() === normalizedLang);
    
    if (matchingKey) {
      console.log('[EN Form Config] Found via explicit key comparison:', matchingKey);
      config = enFormConfigs[matchingKey];
    }
  }
  
  if (!config) {
    // Enhanced debugging: Log why lookup failed
    console.warn('[EN Form Config] Language lookup failed:', {
      rawInput: language,
      normalized: normalizedLang,
      availableKeys: Object.keys(enFormConfigs),
      configObject: enFormConfigs
    });
    console.warn(`[EN Form Config] Language "${language}" (normalized: "${normalizedLang}") not found, falling back to "en"`);
    return enFormConfigs.en;
  }
  
  // Success: Log which config was found
  console.log('[EN Form Config] Successfully found config:', {
    language: normalizedLang,
    endpoint: config.endpoint,
    formId: config.formId
  });
  
  return config;
}

/**
 * Checks if a language has EN form configuration
 * @param language Language code
 * @returns True if configuration exists
 */
export function hasENFormConfig(language: string): boolean {
  const normalizedLang = language.split('-')[0].toLowerCase();
  return normalizedLang in enFormConfigs;
}

/**
 * Checks if a language has comment field configured
 * @param language Language code
 * @returns True if comment field is configured for the language
 */
export function hasCommentField(language: string): boolean {
  const config = getENFormConfig(language);
  return !!config.commentField;
}

/**
 * Checks if a language has completion field configured
 * @param language Language code
 * @returns True if completion field is configured for the language
 */
export function hasCompletionField(language: string): boolean {
  const config = getENFormConfig(language);
  return !!config.completionField;
}

